import { useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import type {
  NotificationHoursPolicy,
  SetNotificationHours,
} from "@reminder-tracker/backend-contracts";
import {
  getDeliveryCapabilities,
  getNotificationHoursPolicy,
  setNotificationHoursPolicy,
} from "../../backend/functions";

const key = (prefix: string) => `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
export function ProtectedNotificationHours(): React.JSX.Element {
  const [correlationId] = useState(() => key("corr"));
  const capabilities = useQuery(getDeliveryCapabilities, { correlationId });
  const policy = useQuery(
    getNotificationHoursPolicy,
    capabilities?.canManagePolicy === true ? { correlationId } : "skip",
  );
  const save = useMutation(setNotificationHoursPolicy);
  if (capabilities === undefined)
    return <p role="status">Checking notification-hours permission…</p>;
  if (!capabilities.canManagePolicy)
    return (
      <section>
        <h2>Notification hours</h2>
        <p>Changing notification hours requires the delivery policy permission.</p>
      </section>
    );
  if (policy === undefined) return <p role="status">Loading notification hours…</p>;
  return (
    <NotificationHoursEditor
      policy={policy}
      onSave={async (request) => {
        await save(request);
      }}
    />
  );
}

export function NotificationHoursEditor({
  policy,
  onSave,
}: Readonly<{
  policy: NotificationHoursPolicy;
  onSave: (request: SetNotificationHours) => Promise<void>;
}>): React.JSX.Element {
  const [start, setStart] = useState(policy.startLocalTime);
  const [end, setEnd] = useState(policy.endLocalTime);
  const [review, setReview] = useState<SetNotificationHours | null>(null);
  const [pending, setPending] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [feedback, setFeedback] = useState<{ error: boolean; text: string } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const busy = useRef(false);
  const stale = review !== null && policy.revision !== review.expectedRevision;
  async function confirm() {
    if (review === null || (stale && !attempted) || busy.current) return;
    busy.current = true;
    setAttempted(true);
    setPending(true);
    setFeedback(null);
    try {
      await onSave(review);
      setFeedback({
        error: false,
        text: `Notification hours saved: ${review.startLocalTime}–${review.endLocalTime}. Existing confirmed times are unchanged.`,
      });
      setReview(null);
    } catch {
      setFeedback({
        error: true,
        text: "The change could not be confirmed. Your proposed hours are still here. Retry, or go back and review the latest settings.",
      });
    } finally {
      busy.current = false;
      setPending(false);
    }
  }
  return (
    <section className="push-governance" aria-label="Notification hours">
      <h2>Notification hours</h2>
      <p>
        Set the hours people can choose for new reminders and timing changes. Times follow each
        reminder’s time zone.
      </p>
      <p>
        Current hours:{" "}
        <strong>
          {policy.startLocalTime}–{policy.endLocalTime}
        </strong>
      </p>
      {feedback === null ? null : <p role={feedback.error ? "alert" : "status"}>{feedback.text}</p>}
      {review === null ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setAttempted(false);
            setFeedback(null);
            setReview({
              startLocalTime: start,
              endLocalTime: end,
              expectedRevision: policy.revision,
              commandKey: key("cmd"),
              correlationId: key("corr"),
            });
            requestAnimationFrame(() => heading.current?.focus());
          }}
        >
          <h3>Change the allowed hours</h3>
          <label>
            From
            <input
              type="time"
              step="60"
              required
              value={start}
              onChange={(e) => setStart(e.currentTarget.value)}
            />
          </label>
          <label>
            Until, including this time
            <input
              type="time"
              step="60"
              required
              value={end}
              onChange={(e) => setEnd(e.currentTarget.value)}
            />
          </label>
          <p>
            Any minute within these hours is allowed. Use 00:00–23:59 for all day. A range such as
            21:00–07:00 crosses midnight.
          </p>
          <h3>Existing reminders keep their times</h3>
          <p>
            Changing these hours does not move or stop saved notifications. The new hours apply when
            someone creates a reminder or changes its timing. Editing a name or recipient alone
            keeps the confirmed timing.
          </p>
          <button className="primary-action" type="submit">
            Review change
          </button>
        </form>
      ) : (
        <section aria-label="Review notification hours">
          <h3 ref={heading} tabIndex={-1}>
            Review notification hours
          </h3>
          <p>
            Change allowed hours from {policy.startLocalTime}–{policy.endLocalTime} to{" "}
            {review.startLocalTime}–{review.endLocalTime}.
          </p>
          <p>
            This applies to new reminders and timing changes in each reminder’s time zone. Existing
            confirmed times stay unchanged. No reminder details are shown to administrators.
          </p>
          <p>
            {attempted
              ? "Check the result before making another change. Retrying checks the same request."
              : "Nothing has changed yet."}
          </p>
          {stale && !attempted ? (
            <p role="alert">
              The settings changed after you opened this review. Go back and review the latest
              hours.
            </p>
          ) : null}
          <div className="decision-actions">
            <button
              className="primary-action"
              type="button"
              disabled={pending || (stale && !attempted)}
              onClick={() => {
                void confirm();
              }}
            >
              {pending ? "Saving…" : attempted ? "Retry confirmation" : "Confirm change"}
            </button>
            <button
              className="secondary-action"
              type="button"
              disabled={pending}
              onClick={() => {
                setReview(null);
                setFeedback(null);
              }}
            >
              Back to edit
            </button>
          </div>
        </section>
      )}
    </section>
  );
}
